import { type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import PhotoDetails, { type TagType } from '../PhotoDetails';
import PhotoDetailsSection from '../PhotoDetailsSection';
import PhotosSearchBrowseFirmDetailsRenderer from '../PhotosSearchBrowse/PhotosSearchBrowseFirmDetailsRenderer';
import RelatedPhotosRendererSuspense from '../RelatedPhotos/RelatedPhotosRendererSuspense';
import { useTrackSearchBrowsePhotoPageview } from '../helpers/interiorsPhotosTracking';
import classnames from 'classnames';

import styles from './PhotosSearchBrowseModalContainer.scss';

import { type PhotosSearchBrowseModalContainer_photo$key } from './__generated__/PhotosSearchBrowseModalContainer_photo.graphql';

type Props = {
    onClose: () => void;
    isOpen: boolean;
    isDesktop?: boolean;
    photo: PhotosSearchBrowseModalContainer_photo$key | null | undefined;
    showRail?: boolean;
    showRelatedPhotos?: boolean;
    fillBackground?: boolean;
    isMobile?: boolean;
    isLoading?: boolean;
    preferredStyleTag?: TagType | null;
    userId: string;
    children: ReactNode;
};

const PhotosSearchBrowseModalContainer: FC<Props> = ({
    children,
    onClose,
    isOpen,
    isDesktop = false,
    isMobile = false,
    isLoading,
    photo: photoRef,
    showRail = true,
    fillBackground,
    preferredStyleTag,
    showRelatedPhotos = true,
    userId,
}) => {
    const photo = useFragment(
        graphql`
            fragment PhotosSearchBrowseModalContainer_photo on PhotosSearchBrowsePhoto {
                ...PhotosSearchBrowseFirmDetailsRenderer_searchBrowsePhoto
                ...interiorsPhotosTracking_searchBrowsePhoto
                name
                path
                serviceId
                styles {
                    name
                    value
                    urlLabel
                }
                room {
                    value
                    name
                    urlLabel
                }
            }
        `,
        photoRef
    );

    const roomTag = photo?.room;
    const photoRoomName = roomTag?.name || '';
    const photoName = photo?.name || photoRoomName || '';
    const styleTag = preferredStyleTag || photo?.styles?.[0];

    useTrackSearchBrowsePhotoPageview(photo);

    const photoDetails = (
        <PhotoDetails styleTag={styleTag} roomTag={roomTag} photoName={photoName} isInModal />
    );

    const firmDetails = <PhotosSearchBrowseFirmDetailsRenderer searchBrowsePhoto={photo} />;

    let content = null;

    if (showRail) {
        if (isDesktop) {
            content = (
                <PhotoDetailsSection>
                    {photoDetails}
                    {firmDetails}
                </PhotoDetailsSection>
            );
        } else {
            content = (
                <>
                    {photoDetails}
                    <PhotoDetailsSection>{firmDetails}</PhotoDetailsSection>
                </>
            );
        }
    }

    return (
        <ModalContainer
            dataTn="photo-modal-container"
            isOpen={isOpen}
            onClose={onClose}
            modalSize="fullscreen"
        >
            {isOpen && (
                <div
                    className={classnames(styles.wrapper, {
                        [styles.fillBackground]: fillBackground,
                    })}
                >
                    {children}
                    {content}
                    {!isLoading && showRelatedPhotos && (
                        <RelatedPhotosRendererSuspense
                            photoId={photo?.serviceId || ''}
                            isMobile={isMobile}
                            styleTag={styleTag}
                            roomTag={roomTag}
                            userId={userId}
                        />
                    )}
                </div>
            )}
        </ModalContainer>
    );
};

export default PhotosSearchBrowseModalContainer;
